import { createSelectorCreator, defaultMemoize } from 'reselect';
import isEqual from 'lodash.isequal';
import qs from 'qs';
import get from 'lodash.get';
import moment from 'moment';
import { AppState } from 'services/types';

const GENERIC_FILTER_WHITELIST = [
    'pickTypeName',
    'varieties',
    'groups',
    'people',
    'entities',
    'schemas',
    'workTypes',
    'users',
    'roles',
    'search',
    'date',
    'count'
];

const createDeepEqualSelector = createSelectorCreator(defaultMemoize, isEqual);

interface Props {
    match?: any;
}

const selectMatch = (state: Record<string, any>, props: Props): any =>
    props.match;

// HACK using get to make tests pass
const selectLocation = (state: AppState): any =>
    get(state, 'router.location', {});

const selectMatchParams = createDeepEqualSelector(
    selectMatch,
    (matchState) => matchState.params
);

const makeSelectMatchParam = (field: string): any =>
    createDeepEqualSelector(
        selectMatchParams,
        (matchParamsState) => matchParamsState[field]
    );

const selectLocationPathname = createDeepEqualSelector(
    selectLocation,
    (locationState) => locationState.pathname
);

const selectLocationSearch = createDeepEqualSelector(
    selectLocation,
    (locationState) => locationState.search
);

const selectLocationState = createDeepEqualSelector(
    selectLocation,
    (locationState) => locationState.state
);

const makeSelectFilterExist = (regexp: RegExp): any =>
    createDeepEqualSelector(selectLocationSearch, (locationSearchState) =>
        locationSearchState.match(regexp)
    );

const selectQueryString = createDeepEqualSelector(
    selectLocationSearch,
    (locationSearchState) =>
        qs.parse(locationSearchState, {
            ignoreQueryPrefix: true,
            arrayLimit: 10000,
            interpretNumericEntities: true
        })
);

const makeSelectQueryString = (): any => selectQueryString;

const makeSelectQueryStringWhitelist = (): any =>
    createDeepEqualSelector(selectQueryString, (queryStringState) =>
        GENERIC_FILTER_WHITELIST.reduce((object, item) => {
            if (queryStringState[item]) {
                return {
                    ...object,
                    [item]: queryStringState[item]
                };
            }

            return object;
        }, {})
    );

const makeSelectQueryStringValue = (field: string): any =>
    createDeepEqualSelector(
        selectQueryString,
        (queryStringState) => queryStringState[field]
    );

const makeSelectQueryStringNumberValue = (field: string): any =>
    createDeepEqualSelector(
        selectQueryString,
        (queryStringState) =>
            queryStringState[field] && Number(queryStringState[field])
    );

const makeSelectQueryStringBooleanValue = (field: string): any =>
    createDeepEqualSelector(selectLocationSearch, (locationSearchState) => {
        const regex = RegExp(field);
        return regex.test(locationSearchState);
    });

const makeSelectQueryStringValuesCount = (fields: string[]): any =>
    createDeepEqualSelector(selectLocationSearch, (locationSearchState) => {
        return fields.reduce((accumulator, currentValue) => {
            const regex = RegExp(currentValue);
            if (regex.test(locationSearchState)) {
                return accumulator + 1;
            }
            return accumulator;
        }, 0);
    });

const makeSelectQueryStringParamsExist = (
    params: Record<string, string | number | boolean>
): any =>
    createDeepEqualSelector(selectLocationSearch, (locationSearchState) => {
        const paramKeys = Object.keys(params);
        return paramKeys.reduce((accumulator, currentValue) => {
            const paramValue = params[currentValue];
            // Check of param exists
            if (
                !RegExp(`${currentValue}=${paramValue}`).test(
                    locationSearchState
                )
            ) {
                // If param is a falsy check if key exists, and if it doesn't assume true.
                if (!paramValue) {
                    return !RegExp(`${currentValue}=`).test(
                        locationSearchState
                    );
                }
                return false;
            }
            return accumulator;
        }, true);
    });

const makeSelectQueryStringArray = (field: string): any =>
    createDeepEqualSelector(
        makeSelectQueryStringValue(field),
        (queryStringValueState: any) =>
            queryStringValueState ? queryStringValueState.split(',') : []
    );

const makeSelectQueryStringNumberArray = (field: string): any =>
    createDeepEqualSelector(
        makeSelectQueryStringValue(field),
        (queryStringValueState: any) =>
            queryStringValueState
                ? queryStringValueState
                      .split(',')
                      .map((value: any) => Number(value))
                : []
    );

const selectTimePickerFromDate = createDeepEqualSelector(
    makeSelectQueryStringValue('timePicker'),
    (queryStringTimePickerState) => {
        switch (queryStringTimePickerState) {
            case 'today':
                return moment().startOf('day').format();
            case 'yesterday':
                return moment().subtract(1, 'day').startOf('day').format();
            case 'last-7-days':
                return moment().subtract(7, 'day').startOf('day').format();
            case 'last-14-days':
                return moment().subtract(14, 'day').startOf('day').format();
            case 'last-28-days':
                return moment().subtract(28, 'day').startOf('day').format();
            default:
                return undefined;
        }
    }
);

const selectTimePickerToDate = createDeepEqualSelector(
    makeSelectQueryStringValue('timePicker'),
    (queryStringTimePickerState) => {
        switch (queryStringTimePickerState) {
            case 'today':
            case 'last-7-days':
            case 'last-14-days':
            case 'last-28-days':
                return moment().endOf('day').format();
            case 'yesterday':
                return moment().subtract(1, 'day').endOf('day').format();
            default:
                return undefined;
        }
    }
);

export {
    selectLocation,
    selectMatchParams,
    makeSelectMatchParam,
    selectLocationPathname,
    selectLocationSearch,
    selectLocationState,
    makeSelectFilterExist,
    selectQueryString,
    makeSelectQueryString,
    makeSelectQueryStringValue,
    makeSelectQueryStringValuesCount,
    makeSelectQueryStringParamsExist,
    makeSelectQueryStringNumberValue,
    makeSelectQueryStringBooleanValue,
    makeSelectQueryStringArray,
    makeSelectQueryStringNumberArray,
    selectTimePickerFromDate,
    selectTimePickerToDate,
    makeSelectQueryStringWhitelist
};
